/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export const NetworkAccessMode = {
  PublicOnly: "public_only",
  Dual: "dual",
  PrivateOnly: "private_only",
} as const;
/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export type NetworkAccessMode = ClosedEnum<typeof NetworkAccessMode>;

/**
 * The visibility of an MCP server
 */
export const CreateMcpServerFormVisibility = {
  Disabled: "disabled",
  Private: "private",
  Public: "public",
} as const;
/**
 * The visibility of an MCP server
 */
export type CreateMcpServerFormVisibility = ClosedEnum<
  typeof CreateMcpServerFormVisibility
>;

/**
 * Form for creating a new MCP server. Exactly one of remote_mcp_server_id, tunneled_mcp_server_id, toolset_id, or unproxied_mcp_server_id must be provided.
 */
export type CreateMcpServerForm = {
  /**
   * The ID of the environment to associate with the server
   */
  environmentId?: string | undefined;
  /**
   * A human-readable display name for the server
   */
  name: string;
  /**
   * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
   */
  networkAccessMode?: NetworkAccessMode | undefined;
  /**
   * The ID of the remote MCP server to use as the backend
   */
  remoteMcpServerId?: string | undefined;
  /**
   * The ID of the tool variations group enabling MCP tool filtering for this server. Omit to leave filtering disabled.
   */
  toolVariationsGroupId?: string | undefined;
  /**
   * The ID of the toolset to use as the backend
   */
  toolsetId?: string | undefined;
  /**
   * The ID of the tunneled MCP server to use as the backend
   */
  tunneledMcpServerId?: string | undefined;
  /**
   * The ID of the unproxied MCP server to use as the backend
   */
  unproxiedMcpServerId?: string | undefined;
  /**
   * The ID of an existing project- or organization-owned user session issuer to attach. Omit to preserve the legacy create behavior, which mints an issuer for remote and tunneled backends.
   */
  userSessionIssuerId?: string | undefined;
  /**
   * The visibility of an MCP server
   */
  visibility: CreateMcpServerFormVisibility;
};

/** @internal */
export const NetworkAccessMode$outboundSchema: z.ZodMiniEnum<
  typeof NetworkAccessMode
> = z.enum(NetworkAccessMode);

/** @internal */
export const CreateMcpServerFormVisibility$outboundSchema: z.ZodMiniEnum<
  typeof CreateMcpServerFormVisibility
> = z.enum(CreateMcpServerFormVisibility);

/** @internal */
export type CreateMcpServerForm$Outbound = {
  environment_id?: string | undefined;
  name: string;
  network_access_mode?: string | undefined;
  remote_mcp_server_id?: string | undefined;
  tool_variations_group_id?: string | undefined;
  toolset_id?: string | undefined;
  tunneled_mcp_server_id?: string | undefined;
  unproxied_mcp_server_id?: string | undefined;
  user_session_issuer_id?: string | undefined;
  visibility: string;
};

/** @internal */
export const CreateMcpServerForm$outboundSchema: z.ZodMiniType<
  CreateMcpServerForm$Outbound,
  CreateMcpServerForm
> = z.pipe(
  z.object({
    environmentId: z.optional(z.string()),
    name: z.string(),
    networkAccessMode: z.optional(NetworkAccessMode$outboundSchema),
    remoteMcpServerId: z.optional(z.string()),
    toolVariationsGroupId: z.optional(z.string()),
    toolsetId: z.optional(z.string()),
    tunneledMcpServerId: z.optional(z.string()),
    unproxiedMcpServerId: z.optional(z.string()),
    userSessionIssuerId: z.optional(z.string()),
    visibility: CreateMcpServerFormVisibility$outboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      environmentId: "environment_id",
      networkAccessMode: "network_access_mode",
      remoteMcpServerId: "remote_mcp_server_id",
      toolVariationsGroupId: "tool_variations_group_id",
      toolsetId: "toolset_id",
      tunneledMcpServerId: "tunneled_mcp_server_id",
      unproxiedMcpServerId: "unproxied_mcp_server_id",
      userSessionIssuerId: "user_session_issuer_id",
    });
  }),
);

export function createMcpServerFormToJSON(
  createMcpServerForm: CreateMcpServerForm,
): string {
  return JSON.stringify(
    CreateMcpServerForm$outboundSchema.parse(createMcpServerForm),
  );
}
